import { driver } from "driver.js";

export const startIdentifyTour = () => {
  const driverObj = driver({
    showProgress: true,
    showButtons: ["next", "previous", "close"],
    nextBtnText: "Next →",
    prevBtnText: "← Back",
    doneBtnText: "Done",
    steps: [
      {
        element: "#tour-identify-header",
        popover: {
          title: "Identify Your Sheets",
          description:
            "Your Excel file has been scanned. Here you'll tell us what type of data each sheet contains so we can import it correctly.",
          side: "bottom",
          align: "start",
        },
      },
      {
        element: "#tour-identify-sheets",
        popover: {
          title: "Sheet Cards",
          description:
            "Each card is a sheet from your Excel file. Click a card to select it — the blue card is currently active. A blue dot means a sheet is already configured.",
          side: "bottom",
          align: "start",
        },
      },
      {
        element: "#tour-identify-type-select",
        popover: {
          title: "Select Data Type",
          description:
            "Choose what kind of data this sheet contains — Student, Parent, Staff, Fee Arrears, or Ignore to skip it entirely.",
          side: "bottom",
          align: "start",
        },
      },
      {
        element: "#tour-identify-preview",
        popover: {
          title: "Data Preview",
          description:
            "The first 3 rows of the selected sheet are shown here. Use this to confirm you've chosen the correct data type.",
          side: "top",
          align: "start",
        },
      },
      {
        element: "#tour-identify-next",
        popover: {
          title: "Proceed",
          description:
            "Once all sheets are configured, click Next to move on to selecting your header row.",
          side: "top",
          align: "end",
        },
      },
    ],
  });
  driverObj.drive();
};

export const startHeaderCheckTour = () => {
  const driverObj = driver({
    showProgress: true,
    showButtons: ["next", "previous", "close"],
    nextBtnText: "Next →",
    prevBtnText: "← Back",
    doneBtnText: "Done",
    steps: [
      {
        element: "#tour-header-title",
        popover: {
          title: "Select Your Header Row",
          description:
            "Your spreadsheet may have title rows or blank rows before the actual column headers. You need to identify which row has the column names.",
          side: "bottom",
          align: "start",
        },
      },
      {
        element: "#tour-header-table",
        popover: {
          title: "Your Raw Data",
          description:
            "Scroll through the rows below and find the one that looks like a header — containing labels like 'Name', 'Roll No', 'Phone', etc.",
          side: "top",
          align: "start",
        },
      },
      {
        element: "#tour-header-rows",
        popover: {
          title: "Click a Row to Set It as Header",
          description:
            "Hover over any row to see the 'Use this row as header' tooltip. Click it to select it — everything above will be discarded and this row becomes your column names.",
          side: "top",
          align: "start",
        },
      },
    ],
  });
  driverObj.drive();
};

export const startMappingTour = () => {
  const driverObj = driver({
    showProgress: true,
    showButtons: ["next", "previous", "close"],
    nextBtnText: "Next →",
    prevBtnText: "← Back",
    doneBtnText: "Done",
    steps: [
      {
        element: "#tour-mapping-header",
        popover: {
          title: "Map Your Columns",
          description:
            "Connect the columns from your spreadsheet to the fields in our system. We've auto-matched as many as possible using AI.",
          side: "bottom",
          align: "start",
        },
      },
      {
        element: "#tour-mapping-erp-col",
        popover: {
          title: "System Field Name",
          description:
            "These are the fields in our ERP — like Student Name, Roll Number, Date of Birth, Phone Number, etc.",
          side: "bottom",
          align: "start",
        },
      },
      {
        element: "#tour-mapping-status-col",
        popover: {
          title: "Required vs Optional",
          description:
            "Fields marked Required must be mapped before you can proceed. Optional fields can be left unmapped.",
          side: "bottom",
          align: "start",
        },
      },
      {
        element: "#tour-mapping-sample-col",
        popover: {
          title: "Sample Data",
          description:
            "A real value from your spreadsheet is shown here so you can verify the mapping is correct at a glance.",
          side: "bottom",
          align: "start",
        },
      },
      {
        element: "#tour-mapping-map-col",
        popover: {
          title: "Map to Column",
          description:
            "Use this dropdown to choose which column from your file corresponds to this system field. A green checkmark appears once a field is mapped.",
          side: "bottom",
          align: "start",
        },
      },
      {
        element: "#tour-mapping-fixed-col",
        popover: {
          title: "Fixed Value Toggle",
          description:
            "Turn this on to enter a constant value instead of picking a column — useful for fields like Country Code (+91) that are the same for all records.",
          side: "bottom",
          align: "start",
        },
      },
      {
        element: "#tour-mapping-next",
        popover: {
          title: "Proceed to Review",
          description:
            "Once all required fields are mapped, click Next to review your data for errors before the final import.",
          side: "top",
          align: "end",
        },
      },
    ],
  });
  driverObj.drive();
};

export const startReviewTour = () => {
  const driverObj = driver({
    showProgress: true,
    showButtons: ["next", "previous", "close"],
    nextBtnText: "Next →",
    prevBtnText: "← Back",
    doneBtnText: "Done",
    steps: [
      {
        element: "#tour-review-header",
        popover: {
          title: "Review & Fix Data",
          description:
            "Before importing, check your data for errors. Rows with missing or invalid information are highlighted in red.",
          side: "bottom",
          align: "start",
        },
      },
      {
        element: "#tour-review-errors-tab",
        popover: {
          title: "Errors Tab",
          description:
            "This tab shows rows with issues. The number tells you how many rows have missing or invalid data that need fixing.",
          side: "bottom",
          align: "start",
        },
      },
      {
        element: "#tour-review-valid-tab",
        popover: {
          title: "Valid Tab",
          description:
            "These rows are clean and ready to import. Switch here to see how many records are good to go.",
          side: "bottom",
          align: "start",
        },
      },
      {
        element: "#tour-review-table",
        popover: {
          title: "Click to Edit Cells",
          description:
            "Click any red cell to edit it directly. Type the correct value and press Enter or click away to save. Use 'Apply to all empty' to fill the same value in all blank cells in that column.",
          side: "top",
          align: "start",
        },
      },
      {
        element: "#tour-review-next",
        popover: {
          title: "Continue to Import",
          description:
            "Click Next when you're done reviewing. If invalid rows remain, you'll be asked whether to skip them or go back to fix them.",
          side: "top",
          align: "end",
        },
      },
    ],
  });
  driverObj.drive();
};
